/**
 * Part Schedule (isolated module).
 *
 * Read-only list of part-level firm-boundary entries imported from the
 * customer's ERP / middleware. The page is gated by the 'part_schedule'
 * feature flag at nav level; the backend ViewSet additionally gates by
 * the per-customer CustomerImportProfile.part_schedule_module_enabled
 * flag, so if the page is ever reached by a customer that has the module
 * disabled, it renders empty (no error).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Loader2, Search, CalendarClock, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { purchasingAPI } from '../lib/api';
import type { PartScheduleEntry } from '../lib/api';
import { useFormatters } from '../contexts/PreferencesContext';

const PAGE_SIZE = 50;

export default function PartSchedulePage() {
  const { formatDate } = useFormatters();
  const [rows, setRows] = useState<PartScheduleEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean> = {
        page,
        page_size: PAGE_SIZE,
        ordering: 'part_number',
      };
      if (debouncedSearch) params['search'] = debouncedSearch;
      if (activeFilter === 'active') params['active'] = true;
      if (activeFilter === 'inactive') params['active'] = false;

      const res = await purchasingAPI.getPartScheduleEntries(params);
      setRows(res.data.results ?? []);
      setTotal(res.data.count ?? 0);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Failed to load part schedule: ${msg}`);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, activeFilter]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const activeSummary = useMemo(() => {
    const activeCount = rows.filter(r => r.active).length;
    return { active: activeCount, inactive: rows.length - activeCount };
  }, [rows]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <CalendarClock className="h-6 w-6" />
          Part Schedule
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Part-level firm-boundary configuration imported from the ERP. One
          entry per part; firm-boundary values drive how far in the future a
          PO line counts as firm vs forecast.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[240px]">
              <label className="text-xs font-medium text-muted-foreground">
                Search by part number
              </label>
              <div className="relative mt-1">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Part number…"
                  className="pl-8"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Status</label>
              <Select
                value={activeFilter}
                onValueChange={v => {
                  setActiveFilter(v as 'all' | 'active' | 'inactive');
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-[160px] mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Entries</CardTitle>
          <div className="text-xs text-muted-foreground">
            {total.toLocaleString()} total · {activeSummary.active.toLocaleString()} active on this page
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground py-10 text-center">
              No part schedule entries found.
              {debouncedSearch && ' Try clearing your search.'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Part Number</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Schedule Firm (days)</TableHead>
                  <TableHead>Plant</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Last Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.id}>
                    <TableCell className="font-mono text-sm">{row.part_number}</TableCell>
                    <TableCell className="text-sm max-w-[360px] truncate" title={row.description}>
                      {row.description || '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {row.schedule_firm ?? '—'}
                    </TableCell>
                    <TableCell>{row.plant || '—'}</TableCell>
                    <TableCell>
                      <Badge variant={row.active ? 'default' : 'outline'}>
                        {row.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {row.source || '—'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDate(row.updated_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {total > PAGE_SIZE && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
              >
                <ChevronLeft className="h-4 w-4" />
                Prev
              </Button>
              <span className="text-xs text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
